import React, { useState } from 'react'

import clsx from 'clsx'
import ReactDOM from 'react-dom'

import Card from 'components/Card'
import DisplayLabel from 'components/DisplayLabel'
import Grid from 'components/Grid'
import Icon from 'components/Icon'
import Lightbox from 'components/Lightbox'
import { FixedImage } from 'components/NextImage'
import Pricing from 'components/Pricing'
import Review from 'components/Review'
import Tag from 'components/Tag'
import { EventContext, useEventContext } from 'context/EventContext'
import { useListingDetailContext } from 'context/ListingDetailContext'
import { isClient } from 'helpers/canUse'

import TextBase from '../TextBase'

import classes from './Overview.module.scss'

const getLightBoxProps = screenshots => {
  const captions = []
  const sources = []
  screenshots.forEach(screenshot => {
    captions.push(screenshot.caption)
    sources.push(screenshot.embed_url || screenshot.url)
  })

  return {
    captions,
    sources,
  }
}

/** @type {import("./types").default} */
const Overview = ({ title, htmlText, className }) => {
  const { listing } = useListingDetailContext()
  const { sendEvent } = useEventContext()

  const { pricing, screenshots } = listing
  const [lightboxState, setLightboxState] = useState({ toggler: false })
  const lightboxToggleHandler = () => {
    setLightboxState({
      toggler: !lightboxState.toggler,
    })
    if (!lightboxState.toggler) {
      sendEvent({
        name: 'engagement_product_click',
        engagementComponent: 'product_media',
        engagementDestination: 'open_media',
        engagementDetails: 'above-the-fold-image',
      })
    }
  }

  return (
    <section className={clsx('Overview', classes.root, className)}>
      <TextBase title={title} disableHeadingMargin />
      <Grid container>
        <Grid item xs={12} lg={7} className={classes.text}>
          <TextBase htmlText={htmlText} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <FixedImage
            className={classes.screenshotImage}
            src={screenshots[0].url}
            onClick={lightboxToggleHandler}
            width={600}
            height={526}
            quality={70}
            fetchpriority="high"
            alt="Main screenshot provided for the software overview"
          />
          <Card className={classes.card} elevation={0}>
            <div>
              <EventContext
                value={{
                  engagementComponent: 'product_overview',
                }}
              >
                <DisplayLabel label="User rating" />
                <Review.Display
                  href={listing.cta_urls.reviews}
                  rating={listing?.reviews?.by_field?.rating_value}
                  reviewsNumber={listing.num_reviews}
                />
              </EventContext>
            </div>

            <div className={classes.pricingDisplayContainer}>
              <Pricing.DisplayLabel pricing={pricing} labelVariant={'small'} />
              <Pricing.Display pricing={pricing} hidePricingModel />

              <div className={classes.freeTrialTag}>
                <Tag color="teal" size="small" disableRipple>
                  Free Trial Available
                  <Icon icon={['far', 'usd-circle']} size={14} />
                </Tag>
              </div>
            </div>
          </Card>
        </Grid>
      </Grid>
      {isClient &&
        ReactDOM.createPortal(
          <Lightbox toggler={lightboxState.toggler} {...getLightBoxProps(screenshots)} />,
          document?.getElementById('portal_root') || document.body
        )}
    </section>
  )
}

export default Overview
